import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

const AttendanceContext = createContext();

export function AttendanceGateProvider({ children }) {
  const [dailyStatus, setDailyStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);

  // Strike notification tracking
  const [notificationsChecked, setNotificationsChecked] = useState(false);

  // Track current user to detect login/logout changes
  const [currentUserId, setCurrentUserId] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user._id || null;
    } catch {
      return null;
    }
  });

  // Derived state from dailyStatus
  const isCheckedIn = dailyStatus?.checked_in || false;
  const checkedInLocation = dailyStatus?.location?.location_name || null;
  const checkInTime = dailyStatus?.check_in_time || null;
  const hasPendingMissedCheckout = dailyStatus?.has_pending_missed_checkout || false;
  const pendingMissedCheckoutRecord = dailyStatus?.pending_missed_checkout_record || null;
  const strikeCount = dailyStatus?.strike_count || 0;
  const isBlocked = dailyStatus?.is_blocked || false;
  const sessionCount = dailyStatus?.session_count || 0;
  const canBreakShift = dailyStatus?.can_break_shift || false;

  // Fetch daily status from backend
  const fetchDailyStatus = useCallback(async () => {
    const user = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (!user || !token) {
      setDailyStatus(null);
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.get('/attendance/daily/status');
      setDailyStatus(response.data);
    } catch (error) {
      console.error('[AttendanceContext] Failed to fetch daily status:', error.message);
      // Don't clear dailyStatus on transient errors — keep last known state
    } finally {
      setLoading(false);
    }
  }, []);

  // Listen for user changes (login/logout) via storage events
  useEffect(() => {
    const checkUserChange = () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const newUserId = user._id || null;

        if (newUserId !== currentUserId) {
          console.log('[AttendanceContext] User changed:', currentUserId, '->', newUserId);
          setCurrentUserId(newUserId);

          // Reset state on user change
          setDailyStatus(null);
          setShowCheckInModal(false);
          setShowCheckOutModal(false);
          setNotificationsChecked(false);

          // Fetch new user's status if logged in
          if (newUserId) {
            fetchDailyStatus();
          }
        }
      } catch {
        // Ignore parse errors
      }
    };

    // Check immediately on mount
    checkUserChange();

    // Listen for cross-tab storage changes and custom login events
    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'token') {
        checkUserChange();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('user-changed', checkUserChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('user-changed', checkUserChange);
    };
  }, [currentUserId, fetchDailyStatus]);

  // Fetch daily status on mount and every 5 minutes
  useEffect(() => {
    fetchDailyStatus();
    const interval = setInterval(fetchDailyStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUserId, fetchDailyStatus]);

  // Check for strike notifications on app load (runs once)
  useEffect(() => {
    const checkStrikeNotifications = async () => {
      if (notificationsChecked) return;

      try {
        const response = await axiosInstance.get('/notifications/unread', {
          params: { type: 'strike_added' }
        });

        const strikeNotifications = response.data || [];

        strikeNotifications.forEach(notification => {
          toast.warning(notification.message, {
            duration: 8000,
            action: {
              label: 'View',
              onClick: () => {
                window.location.href = '/my-roster?tab=attendance';
              }
            }
          });

          axiosInstance.put(`/notifications/${notification.id}/read`).catch(console.error);
        });

        setNotificationsChecked(true);
      } catch (error) {
        console.debug('Notification check failed:', error.message);
        setNotificationsChecked(true);
      }
    };

    const token = localStorage.getItem('token');
    if (token) {
      checkStrikeNotifications();
    }
  }, [notificationsChecked]);

  const handleCheckInSuccess = () => {
    setShowCheckInModal(false);
    // Refresh daily status to get updated state
    fetchDailyStatus();
  };

  const handleCheckOutSuccess = () => {
    setShowCheckOutModal(false);
    // Refresh daily status to get updated state
    fetchDailyStatus();
  };

  // Manual refresh function exposed to consumers
  const refreshStatus = useCallback(() => {
    fetchDailyStatus();
  }, [fetchDailyStatus]);

  // Convenience alias used by Layout.js
  const openCheckOutModal = useCallback(() => {
    setShowCheckOutModal(true);
  }, []);

  return (
    <AttendanceContext.Provider
      value={{
        dailyStatus,
        isCheckedIn,
        checkedInLocation,
        checkInTime,
        hasPendingMissedCheckout,
        pendingMissedCheckoutRecord,
        strikeCount,
        isBlocked,
        sessionCount,
        canBreakShift,
        showCheckInModal,
        setShowCheckInModal,
        showCheckOutModal,
        setShowCheckOutModal,
        handleCheckInSuccess,
        handleCheckOutSuccess,
        openCheckOutModal,
        refreshStatus,
        loading,
        // Backwards-compat stubs for consumers not yet migrated
        // (Layout.js, MyRoster.js, AttendanceGate.js)
        activeShift: null,
        enforcementLevel: 'disabled',
        showBanner: false,
        dismissBanner: () => {},
        rosterStatus: null,
        nudgeCheckIn: () => {},
        isSplitShift: false,
        completedLocations: [],
        currentLocationId: null,
        pendingLocations: [],
        attemptAction: (cb) => { cb(); return true; }
      }}
    >
      {children}
    </AttendanceContext.Provider>
  );
}

export const useAttendanceGate = () => {
  const context = useContext(AttendanceContext);
  if (!context) {
    throw new Error('useAttendanceGate must be used within AttendanceGateProvider');
  }
  return context;
};

// Alias for cleaner imports in new code
export const useAttendance = useAttendanceGate;
